/* Melco Fusion "personalize as a toggle" workflow (Text Connects).
 *
 * With the toggle ON the Add to Cart button(s) become a "Personalize" button
 * that opens the Fusion configurator. When the shopper clicks FINISH in the
 * configurator, the app writes its line item properties into the product form
 * and we submit that form (ajax add to cart via the theme's <product-form>),
 * then reset the page back to its initial state.
 *
 * Relies on the Melco Fusion "Personalization button" app block being present
 * in the same product section (it holds the app config and does the heavy lifting).
 */
if (!customElements.get('personalize-toggle')) {
  class PersonalizeToggle extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
      this.onSectionClick = this.onSectionClick.bind(this);
      this.onCartAdd = this.onCartAdd.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.installConfiguratorHook = this.installConfiguratorHook.bind(this);
      this.patched = new WeakSet();
      this.pendingSubmit = false;
    }

    connectedCallback() {
      this.section = this.closest('.section-main-product') || this.closest('.shopify-section') || document;
      this.productForm = this.section.querySelector('product-form');
      this.input = this.querySelector('.personalize-toggle__input');
      this.buttonLabel = this.dataset.buttonLabel || 'Personalize';

      if (!this.productForm || !this.input) return;

      this.section.classList.add('has-personalize-toggle');
      this.placeRelativeToBuyButtons();

      this.input.addEventListener('change', this.onToggle);
      this.section.addEventListener('click', this.onSectionClick, true);
      this.section.addEventListener('on:cart:add', this.onCartAdd);
      this.section.addEventListener('on:cart:error', () => { this.pendingSubmit = false; });
      this.section.addEventListener('on:variant:change', this.onVariantChange);
      document.addEventListener('shopify:section:load', this.installConfiguratorHook);

      // Fusion's button may render after us; keep trying briefly. The toggle
      // stays hidden unless the product is personalizable (Fusion only renders
      // its button for products configured in Melco).
      this.updateVisibility();
      this.installConfiguratorHook();
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        this.updateVisibility();
        if (this.installConfiguratorHook() || tries > 40) clearInterval(timer);
      }, 250);

      const initialBtn = this.fusionButton;
      if (initialBtn) initialBtn.dataset.toggleBound = 'true';

      this.input.checked = this.dataset.defaultOn === 'true';
      this.applyButtonState();
    }

    disconnectedCallback() {
      this.section?.removeEventListener('click', this.onSectionClick, true);
      this.section?.removeEventListener('on:cart:add', this.onCartAdd);
      this.section?.removeEventListener('on:variant:change', this.onVariantChange);
      document.removeEventListener('shopify:section:load', this.installConfiguratorHook);
    }

    /* ----- helpers ----- */

    /**
     * Move the toggle directly above or below the buy-buttons block, per the
     * block's "Position" setting, regardless of where it sits in block order.
     */
    placeRelativeToBuyButtons() {
      const buyButtons = this.mainForm?.closest('.input-row');
      if (!buyButtons || buyButtons.parentElement !== this.parentElement) return;
      if (this.dataset.position === 'above') {
        if (this.nextElementSibling !== buyButtons) buyButtons.before(this);
      } else if (this.previousElementSibling !== buyButtons) {
        buyButtons.after(this);
      }
    }

    get isPersonalizable() {
      return Boolean(this.fusionButton);
    }

    /**
     * Show the toggle only when the Melco Fusion button exists in this section,
     * i.e. the product is set up for personalization. Always visible in the
     * theme editor so the block can be configured.
     */
    updateVisibility() {
      const designMode = Boolean(window.Shopify && window.Shopify.designMode);
      const show = designMode || this.isPersonalizable;
      if (show) {
        this.removeAttribute('hidden');
      } else {
        this.setAttribute('hidden', '');
      }
      this.section.classList.toggle('is-personalizable', this.isPersonalizable);
      return show;
    }

    get isOn() {
      return Boolean(this.input && this.input.checked && this.isPersonalizable);
    }

    get fusionButton() {
      return this.section.querySelector('.melco-fusion-personalization-button');
    }

    get mainForm() {
      return this.productForm.querySelector('.js-product-form');
    }

    get addToCartButtons() {
      return [...this.section.querySelectorAll('.add-to-cart')];
    }

    /**
     * Swap Add to Cart <-> Personalize label on every ATC button in the section
     * (main form + sticky ATC). Safe to call repeatedly.
     */
    applyButtonState() {
      // Notes (final sale / shipping / custom) only show while personalization is on.
      this.classList.toggle('is-on', this.isOn);

      this.addToCartButtons.forEach((btn) => {
        if (btn.tagName !== 'BUTTON') return;
        const canPersonalize = this.isOn && !btn.disabled && this.fusionButton;
        if (canPersonalize) {
          if (!btn.dataset.personalizeOriginalText) {
            btn.dataset.personalizeOriginalText = btn.textContent.trim();
          }
          btn.textContent = this.buttonLabel;
          btn.classList.add('is-personalize');
        } else if (btn.classList.contains('is-personalize')) {
          btn.textContent = btn.dataset.personalizeOriginalText || btn.dataset.addToCartText || btn.textContent;
          delete btn.dataset.personalizeOriginalText;
          btn.classList.remove('is-personalize');
        }
      });
    }

    /**
     * Remove everything Fusion wrote into the product form(s) so the page is
     * back to a fresh, un-personalized state.
     */
    clearPersonalization() {
      const forms = [...document.querySelectorAll('form')].filter((f) => f.querySelector('[name="form_type"][value="product"]'));
      forms.forEach((form) => {
        const previewProps = form.querySelector('input[name="properties[_melco_preview_properties]"]');
        if (previewProps && previewProps.value) {
          try {
            JSON.parse(previewProps.value).forEach((name) => {
              form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`)?.remove();
            });
          } catch (e) { /* ignore */ }
        }
        form.querySelectorAll('input[name^="properties[_melco_"]').forEach((el) => el.remove());
        form.querySelectorAll('input[type="file"][name^="properties["]').forEach((el) => el.remove());
        form.querySelectorAll('input[data-personalize-toggle-prop]').forEach((el) => el.remove());
      });

      // Reset the app's own UI (hidden, but keep its state consistent).
      const fusionBtn = this.fusionButton;
      if (fusionBtn) {
        const label = fusionBtn.querySelector('span');
        if (label && fusionBtn.dataset.label) label.textContent = fusionBtn.dataset.label;
      }
      this.section.querySelectorAll('.mfc__preview-thubmnails, .mfc__preview-thumbnails').forEach((ul) => { ul.innerHTML = ''; });
      this.section.querySelector('.mfc__preview-thumbnails-wrapper')?.classList.add('hidden');
    }

    /* ----- events ----- */

    onToggle() {
      if (!this.isOn) this.clearPersonalization();
      this.applyButtonState();
    }

    onVariantChange() {
      // Combined listings: a colour change can swap in a different product, and
      // with it a different (or no) Melco Fusion block. Re-sync Fusion + toggle.
      this.syncFusion();
      // The theme re-renders the ATC button on variant change.
      this.applyButtonState();
      setTimeout(() => this.applyButtonState(), 50);
    }

    /**
     * After the theme swaps in another product's Melco Fusion block (combined
     * listings), make sure Fusion's JS is bound to the new button, then update
     * the toggle's visibility / button state.
     */
    syncFusion() {
      const fusionBtn = this.fusionButton;

      if (!fusionBtn) {
        // Product is not personalizable: drop any personalization and hide.
        if (this.input.checked) {
          this.input.checked = false;
          this.clearPersonalization();
        }
        this.updateVisibility();
        this.applyButtonState();
        return;
      }

      if (fusionBtn.dataset.toggleBound !== 'true') {
        fusionBtn.dataset.toggleBound = 'true';
        this.loadFusionConfigurator().then(() => {
          // Re-runs Fusion's own init against the newly inserted button.
          if (typeof window.melcoConfiguratorIsLoaded === 'function') {
            window.melcoConfiguratorIsLoaded();
          }
          this.installConfiguratorHook();
          this.updateVisibility();
          this.applyButtonState();
        });
      }

      this.updateVisibility();
      this.applyButtonState();
    }

    /**
     * Fusion's app block includes its configurator <script> tag, but scripts
     * inserted via the theme's section swap never execute. Load it ourselves if
     * the initial product was not personalizable.
     */
    loadFusionConfigurator() {
      if (window.MelcoConfigurator) return Promise.resolve();
      if (PersonalizeToggle.configuratorPromise) return PersonalizeToggle.configuratorPromise;

      const src = this.section.querySelector('script[src*="melco-configurator"]')?.getAttribute('src')
        || 'https://fusion-configurator.melcocloud.com/javascript/melco-configurator.js';

      PersonalizeToggle.configuratorPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => { PersonalizeToggle.configuratorPromise = null; resolve(); };
        document.head.appendChild(script);
      });
      return PersonalizeToggle.configuratorPromise;
    }

    /**
     * Capture-phase click on any ATC button while the toggle is on: open the
     * configurator instead of submitting the form.
     */
    onSectionClick(evt) {
      if (!this.isOn) return;
      const btn = evt.target.closest('.add-to-cart');
      if (!btn || btn.tagName !== 'BUTTON' || !this.section.contains(btn)) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      const fusionBtn = this.fusionButton;
      if (!fusionBtn) return; // fall through to normal add to cart

      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Same validation the theme performs before adding to cart.
      const form = this.mainForm;
      if (typeof this.productForm.validate === 'function' && !this.productForm.validate()) {
        theme?.scrollToRevealElement?.(this.productForm);
        return;
      }
      if (form && !form.reportValidity()) return;

      this.installConfiguratorHook();
      fusionBtn.click();
    }

    onCartAdd() {
      if (!this.pendingSubmit) return;
      this.pendingSubmit = false;
      // Start fresh: toggle back to its default state, properties cleared, ATC restored.
      this.input.checked = this.dataset.defaultOn === 'true';
      this.clearPersonalization();
      this.applyButtonState();
    }

    /* ----- Fusion configurator hook ----- */

    /**
     * Wrap MelcoConfiguratorInstance.start so we are notified after Fusion's own
     * onComplete has finished writing the session/preview properties to the form.
     * Returns true once the hook is installed.
     */
    installConfiguratorHook() {
      const configurator = window.MelcoConfiguratorInstance;
      if (!configurator || typeof configurator.start !== 'function') return false;
      if (this.patched.has(configurator)) return true;

      const toggle = this;
      const originalStart = configurator.start;

      configurator.start = function (productToken, apiKey, options) {
        if (!toggle.isOn || typeof options?.onComplete !== 'function') {
          return originalStart.apply(this, arguments);
        }

        const originalComplete = options.onComplete;
        const wrapped = {
          ...options,
          async onComplete(...args) {
            // Fusion: writes _melco_session/_melco_pid, downloads previews, sets file inputs.
            const result = await originalComplete.apply(this, args);
            toggle.submitAfterPersonalization();
            return result;
          }
        };
        return originalStart.call(this, productToken, apiKey, wrapped);
      };

      this.patched.add(configurator);
      return true;
    }

    async submitAfterPersonalization() {
      const form = this.mainForm;
      if (!form || !this.isOn) return;
      this.pendingSubmit = true;

      // Shared key so the personalized item and its upcharge can be linked later
      // (bundling extension, order review). Leading underscore hides it in the cart.
      const groupId = String(Date.now());
      this.setFormProperty(form, PersonalizeToggle.GROUP_PROPERTY, groupId);

      // Add the upcharge first so the theme's own submit (and its cart drawer
      // refresh) shows both lines together.
      await this.addUpchargeItem(form, groupId);

      // Give the configurator a tick to close before the cart drawer opens.
      setTimeout(() => {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }, 50);
    }

    /**
     * Set (or create) a hidden line item property on the product form. Inputs we
     * create are tagged so clearPersonalization() removes them again.
     */
    setFormProperty(form, name, value) {
      let input = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.personalizeToggleProp = 'true';
        form.appendChild(input);
      }
      input.value = value;
    }

    /**
     * Add the personalization upcharge product to the cart, linked to the
     * personalized item via the shared group property. Failures are logged but
     * never block the main item from being added.
     */
    async addUpchargeItem(form, groupId) {
      const variantId = parseInt(this.dataset.upchargeVariantId, 10);
      if (!variantId) return;

      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
      const route = (window.theme?.routes?.cartAdd || '/cart/add').replace(/\.js$/, '');

      const properties = {};
      properties[PersonalizeToggle.GROUP_PROPERTY] = groupId;
      if (this.dataset.productHandle) properties._personalized_product = this.dataset.productHandle;
      if (this.dataset.productTitle) properties._personalized_product_title = this.dataset.productTitle;

      try {
        const response = await fetch(`${route}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] })
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          console.error('[personalize-toggle] Upcharge add failed:', data.description || data.message || response.status);
        }
      } catch (error) {
        console.error('[personalize-toggle] Upcharge add failed:', error);
      }
    }
  }

  PersonalizeToggle.GROUP_PROPERTY = '_personalization_group';

  customElements.define('personalize-toggle', PersonalizeToggle);
}
